import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import lookupStore from "./lookupStore"
import { LineupStatusType } from "../types/enum";
import { legalEntityHasSuper } from "../helper/legalEntityHelper";
import profileStore from "./profileStore";
import bookingRequestStore from "./bookingRequestStore";
import superCalculationHelper from "../helper/superCalculationHelper"
class LineupStore {
    @observable openSnackbar = false;
    @observable snackbarMessage = "";
    @observable errors = undefined;
    @observable type = 0;
    @observable bookingId = undefined;
    @observable lineups = [];
    @observable originalLineups = []; // Store original lineup data for status lookups when changing players
    @observable instrumentList = [];

    @observable invitedEmail = "";
    @observable invitedName = "";
    @observable actInstruments = [];
    @observable invitedInstrument = null;

    @observable newPlayerLineup = undefined;
    @observable newPlayerInstruments = undefined;
    @observable errors = undefined;

    @observable isLoadingLineup = false;
    @observable lastEditedLineup = null
    @action
    addError(fieldKey, errorMessage) {
        if (!this.errors) {
            this.errors = [];
        }
        this.errors[fieldKey] = errorMessage;
    }

    @action
    removeLineup(lineup) {
        if (!lineup || !this.lineups.includes(lineup) ||
            lineup.type !== 0 || lineup.status === LineupStatusType.Removed.ordinal) {
            return false;
        }
        lineup.status = LineupStatusType.Removed.ordinal;
        return true;
    }

    @action
    setNewPlayerLineup(newPlayer) {
        this.newPlayerLineup = newPlayer;
    }

    @action
    setNewPlayerInstruments(instruments) {
        this.newPlayerInstruments = instruments;
    }

    @action
    resetNewLineupData() {
        this.newPlayerLineup = undefined;
        this.newPlayerInstruments = undefined;
    }

    @action
    addNewPlayerToLineup(id) {
        this.errors = undefined;
        if (!this.newPlayerLineup) {
            this.addError('performer', "Performer is required!");
        }
        const actAdminId = bookingRequestStore.booking?.act?.actAdminId;
        if (
            bookingRequestStore.booking?.isPayByActOwner &&
            this.newPlayerLineup?.id !== undefined &&
            this.newPlayerLineup?.id !== null &&
            actAdminId !== undefined &&
            actAdminId !== null &&
            String(this.newPlayerLineup.id) === String(actAdminId)
        ) {
            this.addError('performer', "The Act Owner cannot be added as a performer.");
        }
        if (!this.newPlayerInstruments) {
            this.addError('instrument', "Instrument/Skill Set(s) is required!");
        }
        if (!this.errors) {
            this.lineups.push(
                {
                    profileId: this.newPlayerLineup.id,
                    profile: this.newPlayerLineup,
                    name: lookupStore.getInstrumentsName(this.newPlayerInstruments),
                    bookingId: id ? id : this.lineups[0].bookingId,
                    fee: 0,
                    type: 0,
                    rank: this.lineups.length + 1,
                    actRosterId: undefined
                }
            )
            return Promise.resolve();
        }
        return Promise.reject();
    }

    @observable additionalPlayer = undefined;

    @action
    setAdditionalPlayer(player) {
        this.additionalPlayer = player;
    }

    @action
    setInstrumentList(newList) {
        this.instrumentList = newList;
    }

    @action
    reset() {
        this.lineups = [];
        this.originalLineups = [];
        this.openSnackbar = false;
        this.snackbarMessage = "";
        this.errors = undefined;
    }

    @action
    changePlayerAtIndex(index, profileId, profile) {
        if (this.lineups[index]) {
            const currentLineup = this.lineups[index];
            
            // Store acceptedProfileId if current player had accepted (for potential swap back)
            if (currentLineup.status === LineupStatusType.Accepted.ordinal) {
                currentLineup.acceptedProfileId = currentLineup.profileId;
            }
            
            // Determine new status: if swapping back to the player who accepted, restore their status
            let newStatus = LineupStatusType.New.ordinal;
            if (currentLineup.acceptedProfileId && currentLineup.acceptedProfileId === profileId) {
                newStatus = LineupStatusType.Accepted.ordinal;
            }
            
            // Keep the SAME lineup entry (with its database ID), just swap the player
            // This ensures the backend updates the existing entry rather than creating a new one
            this.lineups[index] = {
                ...currentLineup,
                profileId: profileId,
                profile: profile,
                status: newStatus
                // Preserves: id, fee, name (instrument), type, etc.
            };
        }
    }

    @action
    load(bookingId) {
        this.bookingId = bookingId;
        this.isLoadingLineup = true;
        return agent.Booking.loadLineup(bookingId).then(action(async lineups => {
            if (lineups && lineups.length > 0 && lineups !== undefined) {
                // Phase 1 Optimization: Parallel profile mapping
                // Collect all profiles (lineup + roster) for parallel processing
                var allProfilePromises = [];
                
                for (var i = 0; i < lineups.length; i++) {
                    var data = lineups[i];
                    data.expanded = false;
                    data.fee = !isNaN(data.fee) ? Number(data.fee) : 0;
                    
                    // Queue lineup profile for parallel loading
                    if (data.profile) {
                        allProfilePromises.push(
                            profileStore.mapAdditionalProfileDataAsyncLight(data.profile)
                        );
                    }
                    
                    // Queue all roster profiles for parallel loading
                    if (data.actRoster && data.actRoster.rosters) {
                        for (var j = 0; j < data.actRoster.rosters.length; j++) {
                            if (data.actRoster.rosters[j] && data.actRoster.rosters[j].profile) {
                                allProfilePromises.push(
                                    profileStore.mapAdditionalProfileDataAsyncLight(data.actRoster.rosters[j].profile)
                                );
                            }
                        }
                    }
                }
                
                // Execute all profile mappings in parallel
                await Promise.all(allProfilePromises);
                
                runInAction(() => {
                    this.lineups = lineups;
                    // Store a deep copy of original lineups for status lookups when changing players
                    this.originalLineups = lineups.map(l => ({ ...l }));
                })
            } else {
                this.lineups = []
            }
            if (this.additionalPlayer) {
                let lineup = this.lineups.filter(x => x.profileId === this.additionalPlayer.id);
                if (lineup.length === 0 && this.lineups.length > 0) {
                    // Guard: only add additional player if lineups exist
                    runInAction(() => {
                        this.lineups.push({
                            profileId: this.additionalPlayer.id,
                            profile: this.additionalPlayer,
                            name: "Additional Player",
                            bookingId: bookingId, // Use param instead of this.lineups[0].bookingId to avoid out-of-bounds
                            fee: 0,
                            type: 0,
                            rank: this.lineups.length + 1,
                            actRosterId: undefined,
                            status: LineupStatusType.New.ordinal
                        })

                    })
                } else {
                    runInAction(() => {
                        for (let i = 0; i < this.lineups.length; i++) {
                            if (this.lineups[i].profileId == this.additionalPlayer.id && this.lineups[i].status) {
                                this.lineups[i].status = LineupStatusType.New.ordinal
                                this.lineups[i].id = undefined
                            }
                        }
                    })
                }
                this.additionalPlayer = undefined;
            }
            return this.lineups;
        }))
            .finally(
                action(() => {
                    this.isLoadingLineup = false;
                })
            );
    }

    @action
    setExpand(instrument) {
        instrument.expanded = !instrument.expanded;
    }

    @action
    setPlayer(lineup, player) {
        if (lineup.status === LineupStatusType.Accepted.ordinal) {
            lineup.acceptedProfileId = lineup.profileId;
        }

        if (lineup.acceptedProfileId && lineup.acceptedProfileId === player.profileId) {
            lineup.status = LineupStatusType.Accepted.ordinal;
        } else {
            lineup.status = LineupStatusType.New.ordinal;
        }

        lineup.profile = player.profile;
        lineup.profileId = player.profileId;
    }

    @action
    submit(data) {
        this.errors = undefined;
        const booking = {
            id: this.bookingId,
            actFee: data.actFee,
            percentageFeeSuper: data.percentageFeeSuper,
            lineups: this.lineups,
            includeSuper: data.includeSuper,
            isPayByActOwner: data.isPayByActOwner,
            isSuperFee: data.isSuperFee
        };
        return this.saveLineups(booking);
    }

    @action
    setManualLineupFee(fee, index) {
        this.lineups.forEach(action(
            (lineup, idx) => {
                if (idx === index) {
                    lineup.manualFee = fee;
                }
            }
        ))
    }

    @action
    doneEditLineupFeeChanges(index) {
        this.lineups.forEach(action(
            (lineup, idx) => {
                if (idx === index) {
                    var newFee = lineup.manualFee || lineup.manualFee === 0 ? lineup.manualFee : lineup.fee;
                    if(newFee != lineup.fee ){
                        lineup.feeEdited = true
                    }
                    lineup.fee = newFee
                    lineup.manualFee = undefined;
                    lineup.editMode = false;
                   
                    this.lastEditedLineup = lineup
                }
            }
        ))
    }

    @action
    toggleLineupEditMode(index) {
        this.lineups.forEach(action(
            (lineup, idx) => {
                if (idx === index) {
                    lineup.editMode = !lineup.editMode;
                }
            }
        ))
    }

    @action
    countSuperFee(booking) {

        const {percentageFeeSuper } = bookingRequestStore;
        var currentProfile = profileStore.getCurrentProfile()
         let totalFee = 0;
       
        // var performanceFee = booking.actFee
        // if(booking.isSuperFee){
        //     performanceFee = parseFloat(booking?.includeSuper ? superCalculationHelper.calculateFeeWithoutSuperWithInclude(booking.actFee, booking.percentageFeeSuper) : booking.actFee).toFixed(2)
        // }
       
         var performanceFee = 0
        if(booking.isPayByActOwner && booking.isSuperFee){
            performanceFee = parseFloat(booking?.includeSuper ? superCalculationHelper.calculateFeeWithoutSuperWithInclude(booking.actFee, booking.percentageFeeSuper) : booking.actFee).toFixed(2)
        }else{
            performanceFee = booking.actFee
        }
        var lineups = booking.lineups
          lineups.forEach(lineup => {
                if (lineup.status !== LineupStatusType.Removed.ordinal) {
                       totalFee += lineup.manualFee === 0 ? lineup.manualFee : lineup.manualFee ? Number(lineup.manualFee) : Number(lineup.fee);
                   }
               });
        var adminFee =  isNaN(totalFee) ? performanceFee: parseFloat(performanceFee - totalFee)
        var isPayByActOwner = booking.isPayByActOwner
        lineups.forEach(lineup => {
            var isAdmin =  currentProfile?.id === lineup?.profile?.id
              lineup.isSuperFee = legalEntityHasSuper(lineup?.profile?.legalEntity)
                if(!lineup.isSuperFee){
                    lineup.superFee = 0
                    return;
                }
              var lineupFee = parseFloat(lineup.manualFee === 0 ? lineup.manualFee : lineup.manualFee ? (lineup.manualFee) : (lineup.fee))
                var superFee = 0;
                var superFeeAdmin = 0;

                var bookingPercent = legalEntityHasSuper(lineup?.profile?.legalEntity) ? percentageFeeSuper : booking.percentageFeeSuper
              if(!isPayByActOwner && !booking.includeSuper) {
                superFee = superCalculationHelper.calculateSuperWithExclude(lineupFee, bookingPercent)
                superFeeAdmin = superCalculationHelper.calculateSuperWithExclude((parseFloat(lineupFee)+parseFloat(adminFee)),bookingPercent )
              }else{
                superFee = superCalculationHelper.calculateSuperWithInclude(lineupFee,bookingPercent)
                superFeeAdmin = superCalculationHelper.calculateSuperWithInclude((parseFloat(lineupFee)+parseFloat(adminFee)),  bookingPercent)
              }
            
              var superFeePrice = isAdmin ? superFeeAdmin : superFee
              lineup.superFee = superFeePrice
        });
        booking.adminFee = adminFee
        return booking
    }
    @action
    saveLineups(booking) {
        var currentProfile = profileStore.getCurrentProfile()
        booking = this.countSuperFee(booking)
        
        return agent.Booking.saveLineups(booking, currentProfile?.id).then(
            action(async lineups => {
                if (lineups && lineups.length > 0 && lineups !== undefined) {
                    var mapped = [];
                    for (var i = 0; i < lineups.length; i++) {
                        var data = lineups[i]
                        await profileStore.mapAdditionalProfileDataAsync(data.profile);
                        mapped.push(data)
                    }
                    runInAction(() => {
                        this.lineups = mapped;
                        // Update original lineups after save to reflect new statuses
                        this.originalLineups = mapped.map(l => ({ ...l }));
                    })
                } else {
                    this.lineups = []
                }
                return bookingRequestStore.load(booking.id).then(res => {
                    return this.lineups;
                })
            }),
            err => {
                return Promise.reject(err?.response?.text || err?.response?.data?.errors || err);
            }
        );
    }
}

export default new LineupStore();
